'use client';

import { Badge } from '@/components/ui/badge';
import {
  Rocket,
  Hammer,
  BarChart3,
  ShoppingBag,
  Target,
  Crown,
  Star,
} from 'lucide-react';

const LEVEL_CONFIG: Record<number, { icon: typeof Rocket; color: string; bg: string }> = {
  1: { icon: Rocket, color: 'text-slate-500', bg: 'bg-slate-100' },
  2: { icon: Hammer, color: 'text-blue-600', bg: 'bg-blue-50' },
  3: { icon: BarChart3, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  4: { icon: ShoppingBag, color: 'text-purple-600', bg: 'bg-purple-50' },
  5: { icon: Target, color: 'text-orange-600', bg: 'bg-orange-50' },
  6: { icon: Crown, color: 'text-amber-600', bg: 'bg-amber-50' },
  7: { icon: Star, color: 'text-yellow-500', bg: 'bg-yellow-50' },
};

interface BusinessLevelProps {
  level: number;
  name: string;
  title: string;
  progress: number;
  nextLevelName?: string | null;
  compact?: boolean;
}

export function BusinessLevel({
  level,
  name,
  title,
  progress,
  nextLevelName,
  compact = false,
}: BusinessLevelProps) {
  const config = LEVEL_CONFIG[level] ?? LEVEL_CONFIG[1];
  const Icon = config.icon;

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <div className={`flex h-7 w-7 items-center justify-center rounded-full ${config.bg}`}>
          <Icon className={`h-3.5 w-3.5 ${config.color}`} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold">Lv.{level}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {name}
          </Badge>
        </div>
        {nextLevelName && (
          <div className="flex-1 ml-1">
            <div className="h-1 w-full rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <div className={`flex h-10 w-10 items-center justify-center rounded-full ${config.bg}`}>
        <Icon className={`h-5 w-5 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">Level {level}</span>
          <Badge variant="outline" className="text-xs">
            {name}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">{title}</p>
        {nextLevelName && (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="h-1.5 flex-1 rounded-full bg-muted overflow-hidden">
              <div
                className="h-full rounded-full bg-primary transition-all duration-700"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="text-[10px] text-muted-foreground shrink-0">
              {progress}% to {nextLevelName}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
